import styles from './EditorMenu.module.css';
import React from 'react';
import { Editor } from '@tiptap/react';
import cn from 'classnames';
import { useTranslate } from '../../../hooks/useTranslate';
import { Button, ButtonTone } from '../../buttons/Button/Button';
import { FormField } from '../FormField/FormField';
import { ColorPalettePicker } from './components/ColorPalettePicker/ColorPalettePicker';
import { EditorConfiguration } from './EditorConfiguration';

export type EditorMenuProps = EditorConfiguration & {
    editor: Editor | null;
    className?: string;
};

export function EditorMenu({
    editor,
    className,
    textStyle,
    headings,
    textColors,
    lists,
    textAlignment,
    resetStyle
}: EditorMenuProps) {
    const { t } = useTranslate();

    if (!editor) return null;

    return (
        <div className={cn(styles.menu, className)}>
            {textStyle && (
                <div className={styles.buttons}>
                    {textStyle.bold && (
                        <Button
                            type="button"
                            tone={ButtonTone.Hoverable}
                            onClick={() => editor.chain().focus().toggleBold().run()}
                            disabled={!editor.can().chain().focus().toggleBold().run()}
                            className={editor.isActive('bold') ? 'is-active' : ''}
                        >
                            <strong>B</strong>
                        </Button>
                    )}
                    {textStyle.italic && (
                        <Button
                            type="button"
                            tone={ButtonTone.Hoverable}
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                            disabled={!editor.can().chain().focus().toggleItalic().run()}
                            className={editor.isActive('italic') ? 'is-active' : ''}
                        >
                            <i>i</i>
                        </Button>
                    )}
                    {textStyle.underline && (
                        <Button
                            type="button"
                            tone={ButtonTone.Hoverable}
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                            disabled={!editor.can().chain().focus().toggleUnderline().run()}
                            className={editor.isActive('underline') ? 'is-active' : ''}
                        >
                            <u>U</u>
                        </Button>
                    )}
                    {textStyle.strike && (
                        <Button
                            type="button"
                            tone={ButtonTone.Hoverable}
                            onClick={() => editor.chain().focus().toggleStrike().run()}
                            disabled={!editor.can().chain().focus().toggleStrike().run()}
                            className={editor.isActive('strike') ? 'is-active' : ''}
                        >
                            <s>S</s>
                        </Button>
                    )}
                </div>
            )}

            {headings && (
                <div className={styles.buttons}>
                    <FormField
                        id="editor-menu-headings"
                        type={'react-select'}
                        className={styles.headings}
                        menuClassName={styles.headingsMenu}
                        disabled={!editor.can().chain().focus().toggleHeading({ level: 1 }).run()}
                        options={[
                            { value: 1, label: <h1>{t('Heading 1')} </h1> },
                            { value: 2, label: <h2>{t('Heading 2')} </h2> },
                            { value: 3, label: <h3>{t('Heading 3')} </h3> },
                            { value: 4, label: <h4>{t('Heading 4')} </h4> },
                            { value: 5, label: <h5>{t('Heading 5')}</h5> },
                            { value: 6, label: <h6>{t('Heading 6')}</h6> },
                            { value: -1, label: t('Paragraph') }
                        ]}
                        value={editor.getAttributes('heading').level || -1}
                        onChange={(_, level) => {
                            if (level === -1) {
                                editor.chain().focus().setParagraph().run();
                                return;
                            }
                            editor.chain().focus().setHeading({ level }).run();
                        }}
                    />
                </div>
            )}

            {lists && (
                <div className={styles.buttons}>
                    <Button
                        type="button"
                        tone={ButtonTone.Hoverable}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        disabled={!editor.can().chain().focus().toggleBulletList().run()}
                        className={editor.isActive('bulletList') ? 'is-active' : ''}
                        icon={'list'}
                    />
                </div>
            )}

            {textColors && (
                <div className={styles.buttons}>
                    <ColorPalettePicker
                        colorPalette={textColors.front || []}
                        value={editor.getAttributes('textStyle').color}
                        className={styles.textColor}
                        onChange={(color) => {
                            if (color) {
                                editor.chain().focus().setColor(color).run();
                            } else {
                                editor.chain().focus().unsetColor().run();
                            }
                        }}
                    >
                        <strong style={{ color: editor.getAttributes('textStyle').color }}>
                            A
                        </strong>
                    </ColorPalettePicker>

                    <ColorPalettePicker
                        colorPalette={textColors.back || []}
                        value={editor.getAttributes('highlight').color}
                        className={styles.textHighlight}
                        onChange={(color) => {
                            if (color) {
                                editor.chain().focus().setHighlight({ color }).run();
                            } else {
                                editor.chain().focus().unsetHighlight().run();
                            }
                        }}
                    >
                        <strong>A</strong>
                    </ColorPalettePicker>
                </div>
            )}

            {textAlignment && (
                <div className={styles.buttons}>
                    <Button
                        type="button"
                        tone={ButtonTone.Hoverable}
                        onClick={() => editor.chain().focus().toggleTextAlign('left').run()}
                        disabled={!editor.can().chain().focus().toggleTextAlign('left').run()}
                        icon={'text_left'}
                    />
                    <Button
                        type="button"
                        tone={ButtonTone.Hoverable}
                        onClick={() => editor.chain().focus().toggleTextAlign('center').run()}
                        disabled={!editor.can().chain().focus().toggleTextAlign('center').run()}
                        icon={'text_center'}
                    />
                    <Button
                        type="button"
                        tone={ButtonTone.Hoverable}
                        onClick={() => editor.chain().focus().toggleTextAlign('right').run()}
                        disabled={!editor.can().chain().focus().toggleTextAlign('right').run()}
                        icon={'text_right'}
                    />
                </div>
            )}

            {resetStyle && (
                <div className={styles.buttons}>
                    <Button
                        tone={ButtonTone.Hoverable}
                        disabled={!editor.can().chain().unsetAllMarks().run()}
                        onClick={() => {
                            editor.chain().focus().unsetAllMarks().run();
                            editor.chain().focus().unsetTextAlign().run();
                        }}
                    >
                        <u>
                            <i>T</i>
                        </u>
                        <sub>x</sub>
                    </Button>
                </div>
            )}
        </div>
    );
}
